/* eslint-disable react/no-multi-comp */
import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state } from 'app/pwa/app.cerebral';

import { Wrapper, ActivitiesContainer, StatsContainer } from './elements';
import Spa16 from 'dc/Spa16';
import Spa80 from 'dc/Spa80';
import PlaHo01 from 'dc/PlaHo01';
import TotalInsights from './TotalInsights';
import AverageInsights from './AverageInsights';
import PersonalInsights from './PersonalInsights';
import ActivitiesInsights from './ActivitiesInsights';
import ParticipationInsights from './ParticipationInsights';
import InsightsHeader from './InsightsHeader';

import { INDICATOR, ICON } from 'resources/constants';
import { useText, useGetAvatarUrl, useConfig, useImage } from 'common/hooks';
import { getIndicator } from 'utils/common';

const deps = {
  uid: state.appContainer.user.uid,
  profiles: state.appContainer.profiles,
  translate: state.challenge.visualisations.translates.insightsTotal,
  indicator: state.challenge.campaign.indicator,
  activitiesCount: state.challenge.stats.total.challenge.activitiesCount,
  totalStats: state.challenge.stats.total.challenge,
  isLoadingSectionsData: state.challenge.isLoading.sectionsData,
  isLoadingChallengeStats: state.challenge.isLoading.totalChallengeStats,
};

export function InsightsContainer({
  uid,
  profiles,
  translate,
  indicator,
  activitiesCount,
  totalStats,
  isLoadingSectionsData,
  isLoadingChallengeStats,
}: typeof deps & ConnectedProps): React.ReactElement {
  const showTotalStats =
    indicator !== INDICATOR.points && translate && translate.data.co2kg;
  const { staticUrl } = useConfig();

  function getTotalLoggedActivities() {
    return Object.keys(activitiesCount || {}).reduce(
      (totalActivities, activityKey) =>
        totalActivities + activitiesCount[activityKey],
      0
    );
  }

  const t = useText();
  const getAvatarUrl = useGetAvatarUrl();
  const [imageUrl] = useImage(getAvatarUrl(profiles[uid]).large);

  function renderPlaceholder() {
    return (
      <Wrapper>
        <Spa16 />
        <PlaHo01
          icon={ICON.sort}
          text={t('challenge.ChaPaD24B.PlaHo01.no_one_logged')}
        />
        <Spa80 />
      </Wrapper>
    );
  }

  function renderInsights() {
    return (
      <>
        <InsightsHeader
          imageUrl={imageUrl}
          title={t('challenge.ChaPaM25B.personal_insights')}
        />
        <StatsContainer>
          <PersonalInsights />
        </StatsContainer>
        <InsightsHeader
          imageUrl={`${staticUrl}/images/challenge/trophy.svg`}
          title={t('challenge.ChaPaM25B.challenge_insights')}
        />
        {showTotalStats && (
          <StatsContainer>
            <TotalInsights translate={translate} />
          </StatsContainer>
        )}
        <ActivitiesContainer>
          <ActivitiesInsights
            totalLoggedActivities={getTotalLoggedActivities()}
          />
        </ActivitiesContainer>
        <StatsContainer>
          <ParticipationInsights />
        </StatsContainer>
        <StatsContainer>
          <AverageInsights indicator={indicator} />
        </StatsContainer>
        <Spa80 />
      </>
    );
  }

  return !isLoadingSectionsData &&
    !isLoadingChallengeStats &&
    getIndicator(totalStats, indicator) > 0
    ? renderInsights()
    : renderPlaceholder();
}

export default connect(deps, InsightsContainer);
